import { type ReactElement, useState } from "react"

import { useQuery } from "react-query"
import { useParams } from "react-router-dom"

import type { AxiosError, AxiosResponse } from "axios"
import type { MRT_PaginationState } from "material-react-table"

import { SimulationOutputTable } from "../../components/tables/SimulationOutputTable"
import { KonanAPI } from "../../services/KonanAPI"
import type { PaginatedWorkflowSimulationOutputList } from "../../types/generated/api/PaginatedWorkflowSimulationOutputList"

/**
 * Renders the SimulationOutputTable component which displays the outputs of a specific simulation.
 *
 * Fetches simulation output data based on the provided simulationId and projectId.
 * Supports pagination and filtering of the simulation outputs.
 *
 * @param {Object} props - The component props
 * @param {string | undefined} props.simulationId - The UUID of the simulation for which outputs are retrieved
 * @returns {ReactElement} - The rendered SimulationOutputTable component
 */

export function SimulationOutput(props: { simulationId: string | undefined }): ReactElement {
  const { simulationId } = props

  const { id: projectId } = useParams<{ id: string }>()

  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })

  const [filterObject, setFilterObject] = useState<object>({})

  // fetch simulations outputs
  const {
    isLoading: isSimulationOutputLoading,
    data: simulationOutput,
    isFetching: isSimulationOutputFetching,
  } = useQuery<AxiosResponse<PaginatedWorkflowSimulationOutputList>, AxiosError>(
    ["simulation-outputs", projectId, simulationId, pagination, filterObject],
    () =>
      KonanAPI.fetchSimulationOutputs({
        projectId: projectId as string,
        simulation_uuid: simulationId as string,
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        filterObject: filterObject,
      }),
    {
      enabled: !!projectId && !!simulationId,
      keepPreviousData: true,
    },
  )

  return (
    <SimulationOutputTable
      data={simulationOutput?.data ?? undefined}
      isLoading={isSimulationOutputLoading}
      isFetching={isSimulationOutputFetching}
      pagination={pagination}
      setPagination={setPagination}
      filterObject={filterObject}
      setFilterObject={setFilterObject}
      uuid={simulationId ?? ""}
    />
  )
}
